import { Fragment, FC, useMemo, useState } from 'react'
// i18n
import { useTranslation } from '@/hooks/useTranslation'
// components
import { Listbox, Transition } from '@headlessui/react'
// types
import { AccountSelectorProps } from './types'

type AccountOption = { value: string; label: string }

export const AccountList: FC<{ options: AccountOption[] }> = ({ options }) => (
  <>
    {options.map(opt => (
      <Listbox.Option
        key={opt.value}
        value={opt.value}
        className={({ active, selected }) =>
          `relative cursor-pointer select-none py-3 px-3 ${
            selected
              ? 'bg-fill-btn-secondary'
              : active
                ? 'bg-fill-input-secondary'
                : ''
          }`
        }
      >
        {({ selected, active }) => (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Account Info */}
              <div className="flex flex-col">
                <span
                  className={`text-sm font-medium ${
                    selected || active
                      ? 'text-text-primary'
                      : 'text-text-tertiary'
                  }`}
                >
                  {opt.label}
                </span>
              </div>
            </div>
          </div>
        )}
      </Listbox.Option>
    ))}
  </>
)

// 固定账户列表组件
export const AccountSelector: FC<AccountSelectorProps> = ({
  selectedAccount,
  onAccountChange,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation()

  // 固定两个选项：现货/合约（多语言）
  const accountOptions = useMemo(
    () => [
      { value: 'spot', label: t('account.spot') },
      { value: 'perpetual', label: t('account.perpetual') },
    ],
    [t]
  )

  const selectedLabel =
    accountOptions.find(o => o.value === (selectedAccount ?? 'spot'))?.label ||
    ''

  // 非受控降级：父组件未传入 selectedAccount/onAccountChange 时使用内部状态
  const [internalValue, setInternalValue] = useState<string>(
    selectedAccount ?? 'spot'
  )
  const value = selectedAccount ?? internalValue
  const handleChange = (v: string) => {
    onAccountChange?.(v)
    if (!selectedAccount) setInternalValue(v)
  }

  return (
    <div className={`relative mb-4 ${className}`}>
      <Listbox value={value} onChange={handleChange} disabled={disabled}>
        {({ open }) => (
          <>
            <Listbox.Button
              className={`
                relative w-full h-9 text-text-primary bg-fill-input-primary
                items-center justify-between flex px-3 rounded-lg
                focus:outline-none 
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-fill-btn-secondary-hover'}
                ${open ? 'ring-1 ring-white' : ''}
              `}
            >
              <span className="text-sm leading-[18px] font-medium">
                {t('account.title')}
              </span>

              <div className="flex items-center space-x-2">
                {/* Current Account */}
                <span className="text-sm text-white ml-1">{selectedLabel}</span>

                {/* Dropdown Arrow */}
                <svg
                  className={`w-3 h-3 ml-2 transition-transform duration-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="11"
                  viewBox="0 0 10 11"
                  fill="none"
                >
                  <g clipPath="url(#clip0_asset_selector)">
                    <path
                      d="M5.32733 7.6716C5.13944 7.83432 4.86056 7.83432 4.67267 7.6716L1.68366 5.08304C1.33371 4.77997 1.54804 4.20508 2.01099 4.20508L7.98901 4.20508C8.45196 4.20508 8.66629 4.77998 8.31634 5.08304L5.32733 7.6716Z"
                      fill="#F9F9F9"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_asset_selector">
                      <rect
                        width="10"
                        height="10"
                        fill="white"
                        transform="translate(0 0.955078)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </Listbox.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Listbox.Options
                className={`
                  absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg
                  bg-fill-input-primary border border-line-divider-secondary shadow-lg
                  focus:outline-none
                `}
              >
                <AccountList options={accountOptions} />
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  )
}
